import React, { Fragment, useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

// Components

import InstructionalText from '../notifications/InstructionalText';
import Form from '@lib/wrappers/Form';
import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';

// ActionCreators

import { changeData } from '@actions/ssap/ssapActionCreators';

// Utils

import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { preventerTools } from '@utils/tools/preventerTools';
import { omit, set } from '@utils/miscellaneous/objectHelpers';

// Constants

import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';
import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { OTHER_PROGRAMS_LIST, OTHER_PROGRAMS_TYPES } from '@constants/ssap/hhmTypes/otherProgramsList';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { selectExchangeAndProgramNames } from '@reducers/appReducer';




/**
 *
 * @param {Object} props
 * @returns {React.Component} - 'Additional Information - Member Enrolled In Other Health Insurance
 *
 */
const Page4_61 = props => {

    const {
        MARKETPLACE_COVERAGE,
        NONE,
        OTHER_COVERAGE
    } = OTHER_PROGRAMS_TYPES;

    const {
        data,
        environment: {
            isState
        },
        form,
        formName,
        householdMembers
    } = props;

    const {
        isFormSubmitted
    } = preventerTools.provideObject(form[formName]);

    const {
        hhmIndex : member,
        currentHhm
    } = props;

    const {
        healthCoverage : {
            currentlyHasHealthInsuranceIndicator,
            currentOtherInsurance: {
                otherStateOrFederalPrograms = []
            }
        },
        name
    } = currentHhm;

    const { exchangeName, exchangeMedicaid, exchangeChip, exchangeMedicaidOrChip } = useSelector(selectExchangeAndProgramNames);
    const currentEmployerInsurance = (
        currentEmployerArr =>
            currentEmployerArr.length > 0 ?
                currentEmployerArr[0].currentEmployerInsurance :
                {})(householdMembers[member].healthCoverage.currentEmployer);

    const otherInsurance = householdMembers[member]
        .healthCoverage
        .otherInsurance;

    const prepareValuesForOtherProgramTypes = () => {

        return OTHER_PROGRAMS_LIST.reduce((programs, program) => {

            const { notSupported, type } = program;

            switch(true) {
                case notSupported:
                case (isState.IL || isState.NJ || isState.PA) && type === MARKETPLACE_COVERAGE:
                    return programs;
                default:
                {
                    const matchingProgramEligibility = (pr => pr ? pr.eligible : false)(otherStateOrFederalPrograms
                        .find(pr => pr.type === type));

                    return [
                        ...programs,
                        {
                            ...program,
                            eligible: matchingProgramEligibility
                        }
                    ];
                }
            }
        }, []);
    };

    const [ otherPrograms, setOtherPrograms ] = useState(prepareValuesForOtherProgramTypes());

    useEffect(() => {

        const newState = { ...data };

        const memberReference = `taxHousehold.householdMember[${ member }].`;

        const normalizedOtherPrograms = otherPrograms
            .reduce((programs, program) => {
                return program.eligible ?
                    [
                        ...programs,
                        omit(program, 'nameId')
                    ] :
                    programs;
            }, []);

        const shouldCleanOtherCoverageDetails = !~normalizedOtherPrograms.findIndex(pr => pr.type === OTHER_COVERAGE);

        set(
            newState,
            memberReference + 'healthCoverage.currentOtherInsurance.otherStateOrFederalPrograms',
            normalizedOtherPrograms
        );

        if(shouldCleanOtherCoverageDetails) {
            set(
                newState,
                memberReference + 'healthCoverage.otherInsurance',
                { ...EMPTY_OBJECTS.OTHER_INSURANCE }
            );
        }

        props.actions.changeData(newState);

    }, [ JSON.stringify(otherPrograms) ]);

    const onFieldChange = (...args) => {

        let [ newData, , , [ name, value ] ] = args;

        const newState = { ...data };

        if(name === 'currentlyHasHealthInsuranceIndicator' && value === false) {
            newData = {
                ...newData,
                healthCoverage: {
                    ...newData.healthCoverage,
                    otherInsurance: { ...EMPTY_OBJECTS.OTHER_INSURANCE },
                    currentOtherInsurance: {
                        ...newData.healthCoverage.currentOtherInsurance
                    }
                }
            };
            setOtherPrograms(otherPrograms => otherPrograms.map(pr => ({
                ...pr,
                eligible: false
            })));
        }

        newState.taxHousehold.householdMember[member] = { ...newData };

        props.actions.changeData(newState);
    };

    const onFieldChange_programType = (...args) => {
        const [ newData ] = args;

        if (newData.type !== NONE) {
            setOtherPrograms(otherPrograms =>
                otherPrograms.map(pr => pr.type === newData.type ?
                    {
                        ...newData,
                        eligible: !newData.eligible
                    } : pr.type === NONE ?
                        {
                            ...pr,
                            eligible: false
                        } : pr
                ));
        } else {
            setOtherPrograms(otherPrograms =>
                otherPrograms.map(pr => {
                    return pr.type !== newData.type ?
                        {
                            ...pr,
                            eligible: false
                        } :
                        {
                            ...newData,
                            eligible: !newData.eligible
                        };
                }));
        }
    };

    const onFieldChange_otherInsurance = newData => {
        const newState = { ...data };

        newState
            .taxHousehold
            .householdMember[member]
            .healthCoverage
            .otherInsurance = newData;

        props.actions.changeData(newState);
    };

    const checkTopLevelValidation = () => {
        const { currentlyHasHealthInsuranceIndicator } = householdMembers[member].healthCoverage;
        return !currentlyHasHealthInsuranceIndicator ? true  : otherPrograms.some(pgm => pgm.eligible);
    };

    return (
        <Fragment>
            <Form
                topLevelValidity = { checkTopLevelValidation() }
                { ...{ formName } }
            >
                <div
                    className = 'fade-in'
                    id = 'aiOtherInsurance'
                >
                    <div className = 'subsection' >
                        <UniversalInput
                            legend = { {
                                legendText: 'ssap.input.legend.is_enrolled',
                                legendTextReplacements: {
                                    personName: (
                                        <strong>
                                            { normalizeNameOfPerson(name) }
                                        </strong>
                                    ),
                                    note: (
                                        <strong>
                                            <FormattedMessage id = 'ssap.input.legend.is_enrolled.marketplace_note' />
                                        </strong>
                                    )
                                },
                                dataInstructions: 'instructional_text.coverage_currently_enrolled'
                            } }
                            label = { {
                                showRequiredIcon: true
                            } }
                            inputData = { {
                                isInputRequired: true,
                                type: INPUT_TYPES.RADIO_BUTTONS,
                                name: 'currentlyHasHealthInsuranceIndicator',
                                value: currentlyHasHealthInsuranceIndicator,
                                options: DROPDOWN_OPTIONS.YES_NO,
                                currentData: householdMembers[member],
                                validationType: VALIDATION_TYPES.CHOSEN,
                                fields: ['healthCoverage'],
                                isInputStandalone: true

                            } }
                            inputActions = { {
                                onFieldChange
                            } }
                            errorClasses=  { ['margin-l-0'] }
                        />

                        {
                            currentlyHasHealthInsuranceIndicator &&

                            <Fragment>
                                <ValidationErrorMessage
                                    isVisible = { isFormSubmitted && !checkTopLevelValidation() }
                                    messageText = { VALIDATION_ERROR_MESSAGES.COVERAGE_TYPE }
                                    errorClasses = { [ 'margin-l-0' ] }
                                />
                                <div className = 'margin-t-30' >
                                    <fieldset className = 'usa-fieldset' >
                                        <legend
                                            data-instructions = 'coverage_type'
                                            className = 'gi-checkbox margin-b-10'
                                        >
                                            <span className = 'required-true' >
                                                <FormattedMessage
                                                    id = 'ssap.additional_info.other_health_coverage.coverage_type'
                                                    values = { {
                                                        personName: (
                                                            <strong>
                                                                { normalizeNameOfPerson(name) }
                                                            </strong>
                                                        )
                                                    } }
                                                />
                                            </span>
                                            <InstructionalText
                                                Component = {
                                                    <span data-instructions = 'instructional_text.coverage_type' />
                                                }
                                                replacements = {{ exchangeName, exchangeMedicaid, exchangeChip, exchangeMedicaidOrChip }}
                                            />
                                        </legend>

                                        <ul className = 'usa-list usa-list--unstyled' >
                                            {
                                                otherPrograms.map(program => {
                                                    return (
                                                        <Fragment key = { program.type } >
                                                            <li>
                                                                <UniversalInput
                                                                    label = { {
                                                                        text: program.nameId
                                                                    } }
                                                                    inputData = { {
                                                                        isInputRequired: false,
                                                                        classes: [ 'layout--01' ],
                                                                        type: INPUT_TYPES.CHECKBOX,
                                                                        name: program.type,
                                                                        checked: program.eligible,
                                                                        value: program.eligible,
                                                                        currentData: cloneObject(program),
                                                                        fields: [ 'healthCoverage' ],
                                                                        isInputStandalone: true
                                                                    } }
                                                                    inputActions = { {
                                                                        onFieldChange: onFieldChange_programType
                                                                    } }
                                                                />
                                                            </li>

                                                            {
                                                                program && program.type === OTHER_COVERAGE && program.eligible &&

                                                                <Fragment>
                                                                    <li>
                                                                        <UniversalInput
                                                                            label = { {
                                                                                text: 'ssap.input.label.insurance_name',
                                                                                labelClasses: [ 'spl__width--01' ]
                                                                            } }
                                                                            inputData = { {
                                                                                isInputRequired: false,
                                                                                type: INPUT_TYPES.REGULAR_INPUT,
                                                                                name: 'insuranceName',
                                                                                helperClasses: [],
                                                                                value: otherInsurance.insuranceName,
                                                                                placeholder: 'ssap.input.placeholder.insurance_name',
                                                                                validationType: VALIDATION_TYPES.NOT_EMPTY,
                                                                                currentData: otherInsurance,
                                                                                fields: [],
                                                                                isInputStandalone: true
                                                                            } }
                                                                            inputActions = { {
                                                                                onFieldChange: onFieldChange_otherInsurance
                                                                            } }
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <UniversalInput
                                                                            label = { {
                                                                                text: 'ssap.input.label.policy_number',
                                                                                labelClasses: [ 'spl__width--01' ]
                                                                            } }
                                                                            inputData = { {
                                                                                isInputRequired: false,
                                                                                type: INPUT_TYPES.REGULAR_INPUT,
                                                                                name: 'policyNumber',
                                                                                helperClasses: [],
                                                                                value: program.name === 'Employer Insurance'
                                                                                    ? currentEmployerInsurance.policyNumber
                                                                                    : otherInsurance.policyNumber,
                                                                                placeholder: 'ssap.input.placeholder.policy_number',
                                                                                validationType: VALIDATION_TYPES.NOT_EMPTY,
                                                                                currentData: program.name === 'Employer Insurance'
                                                                                    ? currentEmployerInsurance
                                                                                    : otherInsurance,
                                                                                fields: [],
                                                                                isInputStandalone: true
                                                                            } }
                                                                            inputActions = { {
                                                                                onFieldChange: onFieldChange_otherInsurance
                                                                            } }
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <UniversalInput
                                                                            legend = { {
                                                                                legendText: 'ssap.input.legend.limited_benefit_coverage'
                                                                            } }
                                                                            label = { {
                                                                                labelClass: [ 'tablet:grid-col-4 gi-radio_buttons gi-grid__layout-inline gi-col-layout__legend padding-r-1' ],
                                                                                ulClass: [ 'tablet:grid-col-6 gi-grid__layout-inline' ],
                                                                                showRequiredIcon: false
                                                                            } }
                                                                            inputData = { {
                                                                                isInputRequired: false,
                                                                                type: INPUT_TYPES.RADIO_BUTTONS,
                                                                                name: 'limitedBenefit',
                                                                                value: otherInsurance.limitedBenefit,
                                                                                options: DROPDOWN_OPTIONS.YES_NO,
                                                                                currentData: otherInsurance,
                                                                                fields: [],
                                                                                isInputStandalone: true

                                                                            } }
                                                                            inputActions = { {
                                                                                onFieldChange: onFieldChange_otherInsurance
                                                                            } }
                                                                        />
                                                                    </li>
                                                                </Fragment>
                                                            }
                                                        </Fragment>
                                                    );
                                                })
                                            }
                                        </ul>
                                    </fieldset>
                                </div>
                            </Fragment>
                        }
                    </div>
                </div>
            </Form>
        </Fragment>
    );
};

const mapStateToProps = state => ({

    data: state.ssap.data,
    environment: state.common.environment,
    form: state.ssap.form,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page4_61);
